import { useMemo } from "react";
import { t } from "ttag";
import { parseUnits } from "viem";
import { useSimulateContract, useWriteContract } from "wagmi";

import { MAX_TO_WITHDRAW, NULL_ADDRESS } from "@/constants/common";
import { CallType } from "@/constants/enum";
import { GAS_WITHDRAW_BASE, GAS_WITHDRAW_QUOTE } from "@/constants/gas";
import { useRx } from "@/contexts/rx";
import { getExecuteParams } from "@/contracts/tx/execute";
import { useFeatureFlags } from "@/hooks/feature";
import { useGas } from "@/hooks/gas";
import { usePushSubject } from "@/hooks/rx";
import type { TokenDetails } from "@/hooks/token";
import { markWritable } from "@/util/core";

export const useWithdrawAll = ({
  address,
  enabled,
  tokenType,
  token,
  unwrap,
}: {
  address?: `0x${string}`;
  enabled?: boolean;
  tokenType: "base" | "quote";
  token?: TokenDetails;
  /** @deprecated fixme decide if it's ok */
  unwrap?: `0x${string}`;
}) => {
  const { error$ } = useRx();
  const flip = useFeatureFlags(address)?.flip;
  const withdrawCallType =
    tokenType === "base" ? CallType.WithdrawBase : CallType.WithdrawQuote;

  const withdrawGas = useGas(
    tokenType ? GAS_WITHDRAW_BASE : GAS_WITHDRAW_QUOTE,
  );

  const withdrawAllArgs = useMemo(() => {
    if (!token) {
      return;
    }

    return markWritable([
      withdrawCallType,
      parseUnits(MAX_TO_WITHDRAW, token.decimals),
      BigInt(0),
      unwrap === token.address,
      NULL_ADDRESS,
    ] as const);
  }, [token, withdrawCallType, unwrap]);

  const params = useSimulateContract({
    ...getExecuteParams(withdrawAllArgs, { version: flip ? "v15" : "v1" }),
    address: enabled ? address : undefined,
    gas: withdrawGas.gas,
    query: {
      enabled: enabled && !!withdrawAllArgs,
    },
    gasPrice: withdrawGas.gasPrice,
    // @ts-ignore
    value: BigInt(0),
  });

  withdrawGas.useError(params.error);
  const tx = useWriteContract();
  usePushSubject(error$, tx.error);
  const symbol = token?.symbol ?? "";
  return { params, title: t`To withdraw ${symbol} from your position`, tx };
};
